import { applyInlineMarkdown } from '../../../utils/pasteUtils';
import { handleIndentKeyDown } from '../../../utils/indentUtils';
import { tryApplyMarkdownShortcuts } from '../utils/markdownShortcuts';
import { handleTextEditingKeys } from '../utils/textEditingKeys';

/**
 * Typing, indent, slash/variable triggers, and paragraph key bindings.
 */
export const useTextBlockInput = ({
  block,
  contentEditableRef,
  contentRef,
  setContent,
  onUpdate,
  onUpdateAndInsert,
  onInsertAfter,
  onDelete,
  onMergeWithPrevious,
  onMergeWithNext,
  onNavigate,
  onEscapeContainer,
  onOpenDoc,
  slashMenuOpenRef,
  tryOpenFromSlashKey,
  tryOpenFromBraceKey,
  updateSlashQuery,
  tryCloseSlashOnBackspace,
}) => {
  const handleChange = (e) => {
    // Convert "**bold**" / "*italic*" / "`code`" as they're completed (Notion-style)
    const value = applyInlineMarkdown(e.target.value);

    if (
      tryApplyMarkdownShortcuts({
        value,
        prevValue: contentRef.current,
        block,
        onUpdate,
        onUpdateAndInsert,
      })
    ) {
      return;
    }

    contentRef.current = value;
    setContent(value);
    onUpdate(block.id, { content: value });
    updateSlashQuery();
  };

  const handleKeyDown = (e) => {
    const el = contentEditableRef.current;

    if (!slashMenuOpenRef.current && handleIndentKeyDown(e, block, onUpdate, el)) return;

    // Open the "/" menu when typed at the start or after a space
    if (tryOpenFromSlashKey(e)) return;

    // "{{" opens the variable picker (the second "{" completes the trigger)
    if (e.key === '{' && !slashMenuOpenRef.current) {
      tryOpenFromBraceKey(e);
      return;
    }

    // While the menu is open it owns Enter/arrows/Escape (capture phase)
    if (slashMenuOpenRef.current && ['Enter', 'ArrowDown', 'ArrowUp', 'Escape'].includes(e.key)) {
      e.preventDefault();
      return;
    }

    // Backspacing the "/" itself closes the menu
    tryCloseSlashOnBackspace(e);

    handleTextEditingKeys(e, {
      el,
      block,
      contentRef,
      setContent,
      onUpdate,
      onUpdateAndInsert,
      onInsertAfter,
      onDelete,
      onMergeWithPrevious,
      onMergeWithNext,
      onNavigate,
      onEscapeContainer,
      slashMenuOpen: slashMenuOpenRef.current,
    });
  };

  // Clicking a mention tag opens that doc (peek overlay in the host)
  const handleEditableClick = (e) => {
    const mention = e.target.closest?.('[data-doc-mention]');
    if (mention && onOpenDoc) {
      e.preventDefault();
      e.stopPropagation();
      const docId = mention.getAttribute('data-doc-id');
      if (docId) onOpenDoc(docId);
    }
  };

  return { handleChange, handleKeyDown, handleEditableClick };
};
